import {useSelector} from 'react-redux';
import {Box, Typography} from '@mui/material';
import Avatar from '@jobseeker/lotus/components/Avatar';
import {getOldLink} from '@jobseeker/lotus/utils';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {BaseSyntheticEvent, useEffect} from 'react';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {handleTracking} from '@jobseeker/common-core/utils';

import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import type {AppState} from '@jobseeker/common-core/types';

type JobStatusBannerProps = {
  setShowProMembershipPrompt: React.Dispatch<React.SetStateAction<boolean>>;
};
const JobStatusBanner = ({
  setShowProMembershipPrompt,
}: JobStatusBannerProps) => {
  const jobInfo: JB = useSelector((state: AppState) => state.jobDetail);
  const {isPro} = useSelector((state: AppState) => state.auth);

  const theme = useWrappedTheme();
  const isAboveMobile = useIsLargeDevice();

  const handleChange = (e: BaseSyntheticEvent) => {
    e?.preventDefault();
  };

  useEffect(() => {
    handleTracking('jsSCBanner_View', {
      event_category: 'UpgradeToPro',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });
  }, []);

  return (
    <Box
      borderRadius={4}
      mb={0.5}
      minHeight={{xxs: 91, md: 66}}
      flexShrink={0}
      display={{xxs: 'block', md: 'flex'}}
      alignItems={{md: 'center'}}
      sx={{
        background:
          'linear-gradient(243deg, rgba(215, 220, 210, 0.96) 1.33%, rgba(215, 220, 210, 0.96) 62.26%, rgba(233, 222, 197, 0.96) 87.65%, rgba(215, 195, 226, 0.96) 100.76%, rgba(255, 255, 255, 0.00) 100.77%)',
      }}
    >
      <Box
        px={{xxs: 1, lg: 2}}
        py={1.5}
        width={'100%'}
        display={{xxs: 'block', md: 'flex'}}
        justifyContent={'space-between'}
      >
        <Box display={'flex'} alignItems={{md: 'center'}}>
          <Avatar
            sx={{width: 40, height: 40}}
            imgUrl={
              !jobInfo?.confidential
                ? jobInfo?.recruiter?.profilePicUrl
                : `${getOldLink('/resources/img/user.jpg')}`
            }
          />
          <Box
            display={'inline-block'}
            pl={{xxs: 0.5, md: 0.5, lg: 1.5}}
            alignSelf={isPro ? 'center' : 'auto'}
          >
            <Typography
              fontSize={{xxs: 10, md: 12, lg: 14}}
              fontWeight={600}
              lineHeight={{xxs: '16px', lg: '20px'}}
              mb={{xxs: 0, md: 0.25, lg: 0.5}}
            >
              {`${
                !jobInfo?.confidential
                  ? jobInfo?.recruiter?.recruiterName
                  : 'Verified Recruiter'
              } has updated your job application status.`}
            </Typography>
            {!isPro && (
              <Typography
                fontSize={{xxs: 10, lg: 14}}
                fontWeight={500}
                lineHeight={{xxs: '16px', lg: '20px'}}
              >
                See the recruiter’s action by upgrading to
                {!isAboveMobile ? <br /> : ' '}
                <b>PRO-Membership!</b>
              </Typography>
            )}
          </Box>
        </Box>
        <Box display={'flex'} ml={{xxs: 6, md: 0}} alignItems={'center'}>
          {!isPro && (
            <Typography
              fontSize={{xxs: 16, md: 18}}
              fontWeight={600}
              lineHeight={'24px'}
              color={'primary'}
              sx={{
                filter: 'blur(5px)',
              }}
              onCut={handleChange}
              onCopy={handleChange}
            >
              STATUS CHANGED
            </Typography>
          )}
          {jobInfo?.recruiterActionText && isPro && (
            <Typography
              borderRadius={0.5}
              variant="subtitle2"
              sx={{
                px: 1.5,
                py: 0.35,
                backgroundColor: 'rgba(145, 184, 121, 0.16)',
                justifyContent: 'center',
                alignSelf: 'center',
                color: 'primary.main',
              }}
            >
              {jobInfo?.recruiterActionText}
            </Typography>
          )}
          {!isPro && (
            <Button
              sx={{
                background: theme.palette.pro.main,
                py: 1,
                px: 2,
                fontSize: 12,
                my: {xxs: 0, md: 0.52},
                ml: {xxs: 2, md: 1, lg: 3.75},
                width: '135px',
              }}
              variant="contained"
              size="small"
              onClick={() => {
                setShowProMembershipPrompt(true);
                handleTracking('jsSCBanner_Click', {
                  event_category: 'UpgradeToPro',
                  UserId: sessionStorage.getItem('userId') || 'NA',
                  Device: sessionStorage.getItem('device') || 'desktop',
                  Device_Type: sessionStorage.getItem('deviceType') || 'web',
                  Status: sessionStorage.getItem('status') || 'LoggedOut',
                });
              }}
            >
              Upgrade to PRO
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default JobStatusBanner;
